import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getProjects, createProject, deleteProject, setProjectStatus, getProjectMeta } from '../api/client';
import { T } from '../assets/theme';
import { ConfirmModal } from '../components/UI';

const EMPTY_PROJECT = {
  name: '', client_id: '', client_name: '', due_date: '', assessor: '',
  verifier: '', obstacle_type: '', description: '',
  project_type: 'aviation_obstacle_assessment',
  airport_name: '', airport_designator: '', survey_company: '', survey_year: '',
  caa_ref: '',
};

const STATUS_TABS = [
  { key: 'pending',   label: 'Pending',     color: '#f5a623' },
  { key: 'assessing', label: 'Assessing',   color: '#3a8ac4' },
  { key: 'qa',        label: 'Under QA',    color: '#9b59b6' },
  { key: 'complete',  label: 'Complete',    color: '#2ec98b' },
  { key: 'on_hold',   label: 'On Hold',     color: '#aaa' },
];

const STATUS_CYCLE  = { pending: 'assessing', active: 'assessing', assessing: 'qa', qa: 'complete', complete: 'pending' };
const NEXT_LABEL    = { pending: '▶ Start Assessing', active: '▶ Start Assessing', assessing: '⬆ Send to QA', qa: '✓ Mark Complete', complete: '↩ Reopen' };
const STATUS_BADGE  = {
  pending:   { bg: 'rgba(245,166,35,0.13)',  color: '#f5a623', label: 'PENDING' },
  assessing: { bg: 'rgba(58,138,196,0.13)',  color: '#3a8ac4', label: 'ASSESSING' },
  qa:        { bg: 'rgba(155,89,182,0.13)',  color: '#9b59b6', label: 'UNDER QA' },
  complete:  { bg: 'rgba(46,201,139,0.13)',  color: '#2ec98b', label: 'COMPLETE' },
  active:    { bg: 'rgba(245,166,35,0.13)',  color: '#f5a623', label: 'PENDING' },
  on_hold:   { bg: 'rgba(200,200,200,0.13)', color: '#aaa',    label: 'ON HOLD' },
};

function canonicalStatus(s) {
  return s === 'active' ? 'pending' : (s || 'pending');
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function Btn({ children, onClick, variant = 'primary', small, disabled, style: sx = {} }) {
  const variants = {
    primary: { bg: T.blue, color: T.white },
    danger:  { bg: 'rgba(232,64,64,0.18)', color: '#e84040' },
    ghost:   { bg: 'rgba(58,138,196,0.09)', color: T.blue },
    success: { bg: 'rgba(46,201,139,0.15)', color: T.safe },
    warning: { bg: 'rgba(245,166,35,0.15)', color: T.warn },
  };
  const v = variants[variant] || variants.primary;
  return (
    <button onClick={onClick} disabled={disabled} style={{
      background: v.bg, color: v.color, border: `1px solid ${v.color}22`,
      borderRadius: 6, padding: small ? '5px 12px' : '8px 18px',
      fontSize: small ? 11 : 13, fontWeight: 600, cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.5 : 1, transition: 'all 0.15s', ...sx,
    }}>{children}</button>
  );
}

function StatusBadge({ status }) {
  const s = STATUS_BADGE[status] || STATUS_BADGE.pending;
  return (
    <span style={{
      padding: '3px 10px', borderRadius: 3,
      border: `1px solid ${s.color}`,
      background: s.bg, color: s.color,
      fontSize: 10, fontWeight: 800, letterSpacing: '0.07em',
    }}>{s.label}</span>
  );
}

// ─── Card view ────────────────────────────────────────────────────────────────
function ProjectCard({ project, onOpen, onDelete, onStatus }) {
  const [hovering, setHovering] = useState(false);
  const onHold = project.status === 'on_hold';
  const nextStatus = STATUS_CYCLE[project.status] || 'assessing';
  const nextLabel  = NEXT_LABEL[project.status]   || '▶ Start Assessing';
  return (
    <div
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      onClick={() => onOpen(project.id)}
      style={{
        background: T.bg1, border: `1px solid ${hovering ? T.blue : T.border}`,
        borderRadius: 10, padding: '20px 22px', cursor: 'pointer',
        transition: 'all 0.2s',
        boxShadow: hovering ? '0 4px 20px rgba(58,138,196,0.15)' : 'none',
        display: 'flex', flexDirection: 'column', gap: 10,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: T.white, flex: 1, paddingRight: 8 }}>
          {project.name}
        </div>
        <StatusBadge status={project.status} />
      </div>

      {project.client_name && (
        <div style={{ fontSize: 12, color: T.muted }}>🏢 {project.client_name}</div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
        {[
          ['👤 Assessor', project.assessor],
          ['✅ Verifier', project.verifier],
          ['📦 Type',     project.obstacle_type],
          ['📅 Due',      project.due_date],
        ].filter(([, v]) => v).map(([label, val]) => (
          <div key={label} style={{ fontSize: 11, color: T.muted }}>
            <span>{label}: </span><span style={{ color: T.white }}>{val}</span>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 4 }}>
        <span style={{ fontSize: 11, color: T.muted }}>
          ⚑ {project.obstacle_count || 0} obstacle{project.obstacle_count !== 1 ? 's' : ''}
        </span>
        <div style={{ display: 'flex', gap: 6 }} onClick={e => e.stopPropagation()}>
          {onHold ? (
            <Btn variant="ghost" small onClick={() => onStatus(project, 'pending')}>▶ Resume</Btn>
          ) : (
            <>
              <Btn variant="ghost" small onClick={() => onStatus(project, nextStatus)}>{nextLabel}</Btn>
              <Btn variant="ghost" small onClick={() => onStatus(project, 'on_hold')}>⏸ Hold</Btn>
            </>
          )}
          <Btn variant="danger" small onClick={() => onDelete(project.id)}>✕</Btn>
        </div>
      </div>
    </div>
  );
}

// ─── Table view ───────────────────────────────────────────────────────────────
function ProjectTable({ projects, onOpen, onDelete, onStatus }) {
  const cols = [
    { label: 'Project Name', key: 'name',           w: '22%' },
    { label: 'Client',       key: 'client_name',    w: '14%' },
    { label: 'Type',         key: 'obstacle_type',  w: '12%' },
    { label: 'Status',       key: 'status',         w: '10%' },
    { label: 'Assessor',     key: 'assessor',       w: '10%' },
    { label: 'Verifier',     key: 'verifier',       w: '10%' },
    { label: 'Due',          key: 'due_date',       w: '9%'  },
    { label: 'Obs.',         key: 'obstacle_count', w: '6%'  },
    { label: '',             key: '_actions',       w: '7%'  },
  ];
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
      <thead>
        <tr style={{ borderBottom: `1px solid ${T.border}` }}>
          {cols.map(c => (
            <th key={c.key} style={{
              width: c.w, padding: '8px 10px', textAlign: 'left',
              fontSize: 10, fontWeight: 700, letterSpacing: '0.08em',
              textTransform: 'uppercase', color: T.muted,
            }}>{c.label}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {projects.map((p, i) => {
          const onHold = p.status === 'on_hold';
          const nextStatus = STATUS_CYCLE[p.status] || 'assessing';
          const nextLabel  = NEXT_LABEL[p.status]   || '▶ Start Assessing';
          return (
            <tr
              key={p.id}
              onClick={() => onOpen(p.id)}
              style={{
                background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)',
                borderBottom: `1px solid ${T.border}22`,
                cursor: 'pointer', transition: 'background 0.12s',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(58,138,196,0.07)'}
              onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)'}
            >
              <td style={{ padding: '10px 10px', fontWeight: 700, color: T.white }}>{p.name}</td>
              <td style={{ padding: '10px 10px', color: T.muted }}>{p.client_name || '—'}</td>
              <td style={{ padding: '10px 10px', color: T.muted }}>{p.obstacle_type || '—'}</td>
              <td style={{ padding: '10px 10px' }}><StatusBadge status={p.status} /></td>
              <td style={{ padding: '10px 10px', color: T.muted }}>{p.assessor || '—'}</td>
              <td style={{ padding: '10px 10px', color: T.muted }}>{p.verifier || '—'}</td>
              <td style={{ padding: '10px 10px', color: T.muted }}>{p.due_date || '—'}</td>
              <td style={{ padding: '10px 10px', color: T.muted, textAlign: 'center' }}>{p.obstacle_count || 0}</td>
              <td style={{ padding: '10px 10px' }} onClick={e => e.stopPropagation()}>
                <div style={{ display: 'flex', gap: 5 }}>
                  {onHold ? (
                    <Btn variant="ghost" small onClick={() => onStatus(p, 'pending')} style={{ fontSize: 10, padding: '3px 8px' }}>
                      ▶ Resume
                    </Btn>
                  ) : (
                    <>
                      <Btn variant="ghost" small onClick={() => onStatus(p, nextStatus)} style={{ fontSize: 10, padding: '3px 8px' }}>
                        {nextLabel}
                      </Btn>
                      <Btn variant="ghost" small onClick={() => onStatus(p, 'on_hold')} style={{ fontSize: 10, padding: '3px 8px' }}>
                        ⏸ Hold
                      </Btn>
                    </>
                  )}
                  <Btn variant="danger" small onClick={() => onDelete(p.id)} style={{ fontSize: 10, padding: '3px 8px' }}>✕</Btn>
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// ─── Create modal ─────────────────────────────────────────────────────────────
function FormField({ label, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5, minWidth: 0 }}>
      <label style={{ fontSize: 11, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</label>
      {children}
    </div>
  );
}
const inputSx = { background: T.bg0, border: `1px solid ${T.border}`, borderRadius: 6, padding: '8px 12px', color: T.white, fontSize: 13, outline: 'none', width: '100%', boxSizing: 'border-box' };

function CreateModal({ meta, onClose, onCreate }) {
  const [form, setForm] = useState(EMPTY_PROJECT);
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  async function submit() {
    if (!form.name.trim()) return;
    setLoading(true);
    try {
      const { data } = await createProject(form);
      onCreate(data);
      onClose();
    } finally { setLoading(false); }
  }

  const Select = ({ field, options }) => (
    <select value={form[field]} onChange={e => set(field, e.target.value)} style={{ ...inputSx }}>
      <option value="">— select —</option>
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  );

  const isOls = form.project_type === 'annex_14_ols_assessment';

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 500 }}>
      <div style={{ background: T.bg1, border: `1px solid ${T.border}`, borderRadius: 12, padding: 32, width: 560, maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto', overflowX: 'hidden', boxSizing: 'border-box' }}>
        <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 24 }}>New Project</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <FormField label="Project Name *">
            <input style={inputSx} value={form.name} onChange={e => set('name', e.target.value)} />
          </FormField>
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 14 }}>
            <FormField label="Client / Company">
              <select value={form.client_id} onChange={e => set('client_id', e.target.value)} style={inputSx}>
                <option value="">— select —</option>
                {(meta.clients || []).map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </FormField>
            <FormField label="Assessor"><Select field="assessor" options={meta.assessors || []} /></FormField>
            <FormField label="Verifier"><Select field="verifier" options={meta.verifiers || []} /></FormField>
            <FormField label="Obstacle Type"><Select field="obstacle_type" options={meta.obstacle_types || []} /></FormField>
            <FormField label="Due Date">
              <input type="date" style={inputSx} value={form.due_date} onChange={e => set('due_date', e.target.value)} />
            </FormField>
          </div>
          <FormField label="Project Type">
            <select value={form.project_type} onChange={e => set('project_type', e.target.value)} style={inputSx}>
              <option value="aviation_obstacle_assessment">Aviation Obstacle Assessment</option>
              <option value="annex_14_ols_assessment">Annex 14 OLS Assessment</option>
            </select>
          </FormField>
          {isOls && (
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 14, padding: '14px 16px', background: 'rgba(58,138,196,0.07)', borderRadius: 8, border: `1px solid rgba(58,138,196,0.2)` }}>
              <FormField label="Airport Name">
                <input style={inputSx} value={form.airport_name} onChange={e => set('airport_name', e.target.value)} />
              </FormField>
              <FormField label="Airport Designator">
                <input style={inputSx} value={form.airport_designator} onChange={e => set('airport_designator', e.target.value)} />
              </FormField>
              <FormField label="Survey Company">
                <input style={inputSx} value={form.survey_company} onChange={e => set('survey_company', e.target.value)} />
              </FormField>
              <FormField label="Survey Year">
                <input style={inputSx} value={form.survey_year} onChange={e => set('survey_year', e.target.value)} />
              </FormField>
            </div>
          )}
          <FormField label="CAA ID / Ref">
            <input style={inputSx} value={form.caa_ref} onChange={e => set('caa_ref', e.target.value)} placeholder="e.g. CAA/OAS/2024/001" />
          </FormField>
          <FormField label="Description">
            <textarea rows={3} style={{ ...inputSx, resize: 'vertical' }} value={form.description} onChange={e => set('description', e.target.value)} />
          </FormField>
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 24, justifyContent: 'flex-end' }}>
          <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn onClick={submit} disabled={loading || !form.name.trim()}>
            {loading ? 'Creating…' : 'Create Project'}
          </Btn>
        </div>
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function ProjectsPage() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [meta, setMeta]         = useState({ assessors: [], verifiers: [], obstacle_types: [] });
  const [loading, setLoading]   = useState(true);
  const [showCreate, setShowCreate]         = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  // — UI state —
  const [activeTab,   setActiveTab]   = useState('pending');
  const [viewMode,    setViewMode]    = useState('card');   // 'card' | 'table'
  const [filterType,  setFilterType]  = useState('');
  const [filterClient,setFilterClient]= useState('');
  const [search,      setSearch]      = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, mRes] = await Promise.all([getProjects(), getProjectMeta()]);
      setProjects(pRes.data.projects);
      setMeta(mRes.data);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleDelete(id) { setDeleteConfirmId(id); }

  async function confirmDelete() {
    if (!deleteConfirmId) return;
    await deleteProject(deleteConfirmId);
    setProjects(p => p.filter(x => x.id !== deleteConfirmId));
    setDeleteConfirmId(null);
  }

  async function handleStatus(project, nextStatus) {
    const { data } = await setProjectStatus(project.id, nextStatus);
    setProjects(p => p.map(x => x.id === data.id ? { ...data, obstacle_count: x.obstacle_count } : x));
  }

  // Unique filter options derived from loaded data
  const clientOptions = useMemo(() => [...new Set(projects.map(p => p.client_name).filter(Boolean))].sort(), [projects]);
  const typeOptions   = useMemo(() => [...new Set(projects.map(p => p.obstacle_type).filter(Boolean))].sort(), [projects]);

  // Counts per tab (before other filters, so counts stay meaningful)
  const tabCounts = useMemo(() => {
    const counts = { pending: 0, assessing: 0, qa: 0, complete: 0, on_hold: 0 };
    projects.forEach(p => { const s = canonicalStatus(p.status); if (counts[s] !== undefined) counts[s]++; });
    return counts;
  }, [projects]);

  // Filtered list for the active tab
  const visible = useMemo(() => projects.filter(p => {
    if (canonicalStatus(p.status) !== activeTab) return false;
    if (filterType   && p.obstacle_type !== filterType)   return false;
    if (filterClient && p.client_name   !== filterClient) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!p.name?.toLowerCase().includes(q) && !p.client_name?.toLowerCase().includes(q)) return false;
    }
    return true;
  }), [projects, activeTab, filterType, filterClient, search]);

  const selectSx = { background: T.bg0, border: `1px solid ${T.border}`, borderRadius: 6, padding: '6px 10px', color: T.white, fontSize: 12, outline: 'none', cursor: 'pointer' };

  return (
    <div style={{ padding: 28, overflowY: 'auto', height: '100%', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: 0 }}>

      {/* ── Header ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800 }}>Projects</h2>
          <div style={{ fontSize: 12, color: T.muted, marginTop: 4 }}>Manage your obstacle safeguarding assessments</div>
        </div>
        <Btn onClick={() => setShowCreate(true)}>+ New Project</Btn>
      </div>

      {/* ── Status tabs ── */}
      <div style={{ display: 'flex', gap: 2, borderBottom: `1px solid ${T.border}`, marginBottom: 18 }}>
        {STATUS_TABS.map(tab => {
          const active = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                background: 'none', border: 'none',
                borderBottom: active ? `2px solid ${tab.color}` : '2px solid transparent',
                padding: '10px 20px', fontSize: 13,
                fontWeight: active ? 700 : 400,
                color: active ? tab.color : T.muted,
                cursor: 'pointer', marginBottom: -1,
                transition: 'color 0.15s',
                display: 'flex', alignItems: 'center', gap: 8,
              }}
            >
              {tab.label}
              <span style={{
                background: active ? `${tab.color}22` : 'rgba(255,255,255,0.06)',
                color: active ? tab.color : T.muted,
                borderRadius: 10, padding: '1px 8px', fontSize: 10, fontWeight: 700,
              }}>
                {tabCounts[tab.key]}
              </span>
            </button>
          );
        })}
      </div>

      {/* ── Toolbar: filters + view toggle ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18, flexWrap: 'wrap' }}>
        <input
          placeholder="Search projects…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ ...selectSx, width: 200 }}
        />

        <select value={filterType} onChange={e => setFilterType(e.target.value)} style={selectSx}>
          <option value="">All Types</option>
          {typeOptions.map(t => <option key={t} value={t}>{t}</option>)}
        </select>

        <select value={filterClient} onChange={e => setFilterClient(e.target.value)} style={selectSx}>
          <option value="">All Clients</option>
          {clientOptions.map(c => <option key={c} value={c}>{c}</option>)}
        </select>

        {(filterType || filterClient || search) && (
          <button
            onClick={() => { setFilterType(''); setFilterClient(''); setSearch(''); }}
            style={{ background: 'none', border: 'none', color: T.muted, fontSize: 11, cursor: 'pointer', textDecoration: 'underline' }}
          >
            Clear filters
          </button>
        )}

        <div style={{ flex: 1 }} />

        {/* View toggle */}
        <div style={{ display: 'flex', border: `1px solid ${T.border}`, borderRadius: 6, overflow: 'hidden' }}>
          {[
            { key: 'card',  icon: '⊞', title: 'Card view' },
            { key: 'table', icon: '☰', title: 'Table view' },
          ].map(v => (
            <button
              key={v.key}
              title={v.title}
              onClick={() => setViewMode(v.key)}
              style={{
                background: viewMode === v.key ? 'rgba(58,138,196,0.18)' : 'transparent',
                border: 'none', borderRight: v.key === 'card' ? `1px solid ${T.border}` : 'none',
                color: viewMode === v.key ? T.blue : T.muted,
                padding: '6px 13px', cursor: 'pointer', fontSize: 15, lineHeight: 1,
              }}
            >
              {v.icon}
            </button>
          ))}
        </div>
      </div>

      {/* ── Content ── */}
      {loading ? (
        <div style={{ color: T.muted, textAlign: 'center', padding: 60 }}>Loading…</div>
      ) : visible.length === 0 ? (
        <div style={{ color: T.muted, textAlign: 'center', padding: 60, fontSize: 13 }}>
          No {STATUS_TABS.find(t => t.key === activeTab)?.label.toLowerCase()} projects
          {(filterType || filterClient || search) ? ' matching the current filters' : ''}.
        </div>
      ) : viewMode === 'card' ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 14 }}>
          {visible.map(p => (
            <ProjectCard
              key={p.id} project={p}
              onOpen={id => navigate(`/projects/${id}`)}
              onDelete={handleDelete}
              onStatus={handleStatus}
            />
          ))}
        </div>
      ) : (
        <div className="dg-panel" style={{ overflow: 'hidden' }}>
          <ProjectTable
            projects={visible}
            onOpen={id => navigate(`/projects/${id}`)}
            onDelete={handleDelete}
            onStatus={handleStatus}
          />
        </div>
      )}

      {showCreate && (
        <CreateModal
          meta={meta}
          onClose={() => setShowCreate(false)}
          onCreate={p => { setProjects(prev => [p, ...prev]); setActiveTab(canonicalStatus(p.status)); }}
        />
      )}

      {deleteConfirmId && (
        <ConfirmModal
          title="Delete Project"
          message="Are you sure you want to delete this project and all its obstacles? This action cannot be undone."
          confirmText="Delete Project"
          onConfirm={confirmDelete}
          onCancel={() => setDeleteConfirmId(null)}
          variant="danger"
        />
      )}
    </div>
  );
}
